import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LiveGame, LiveGamesService, ScoreboardMeta } from '../services/LiveGamesService';
import './InProgressGames.css';
import { getOwnerColorClass } from '../utils/ownerColors';

interface InProgressGamesProps {
  ownerByTeam: Record<string, string>; // e.g., { BUF: 'sam' }
}

type SeasonType = 2 | 3; // 2 = regular, 3 = postseason

const keyFor = (st: SeasonType, wk?: number) => `${st}:${wk ?? 'cur'}`;

const InProgressGames: React.FC<InProgressGamesProps> = ({ ownerByTeam }) => {
  const [meta, setMeta] = useState<ScoreboardMeta | null>(null);
  const [selectedSeasonType, setSelectedSeasonType] = useState<SeasonType>(2);
  const [selectedWeek, setSelectedWeek] = useState<number | undefined>(undefined);
  const [seasonYear, setSeasonYear] = useState<number>(new Date().getFullYear());

  const [games, setGames] = useState<LiveGame[]>([]);
  const [cache, setCache] = useState<Map<string, LiveGame[]>>(new Map());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Track which score cells should flash (e.g., "<gameId>-home" | "<gameId>-away")
  const [flashing, setFlashing] = useState<Set<string>>(new Set());
  // Keep previous scores for change detection between refreshes
  const prevScoresRef = useRef<Map<string, { home: number; away: number }>>(new Map());

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      let data: LiveGame[] = [];
      const st: SeasonType = selectedSeasonType;
      const wk = selectedWeek;
      const sy = seasonYear;
      const ckey = keyFor(st, wk);
      if (cache.has(ckey)) {
        data = cache.get(ckey)!;
      } else {
        data = await LiveGamesService.fetchGamesFor({ seasonYear: sy, seasonType: st, week: wk });
        setCache(prev => {
          const next = new Map(prev);
          next.set(ckey, data);
          return next;
        });
      }

      // Detect score increases and trigger flash for the changed side(s)
      const prevScores = prevScoresRef.current;
      const nextScores = new Map<string, { home: number; away: number }>();

      const changedKeys: string[] = [];
      for (const g of data) {
        const key = g.id;
        const prev = prevScores.get(key);
        if (prev) {
          if (g.homeScore > prev.home) changedKeys.push(`${key}-home`);
          if (g.awayScore > prev.away) changedKeys.push(`${key}-away`);
        }
        nextScores.set(key, { home: g.homeScore, away: g.awayScore });
      }

      // Prime initial scores without flashing on first load
      if (prevScores.size === 0) {
        prevScoresRef.current = nextScores;
      } else if (changedKeys.length > 0) {
        // Add changed keys to flashing set and schedule removal
        setFlashing((prevSet) => {
          const updated = new Set(prevSet);
          for (const k of changedKeys) updated.add(k);
          return updated;
        });
        // Remove each flash after a short timeout
        changedKeys.forEach((k) => {
          setTimeout(() => {
            setFlashing((prevSet) => {
              const updated = new Set(prevSet);
              updated.delete(k);
              return updated;
            });
          }, 1600);
        });
        prevScoresRef.current = nextScores;
      } else {
        prevScoresRef.current = nextScores;
      }

      setGames(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load live games');
    } finally {
      setLoading(false);
    }
  };

  // Parse initial selection from URL or fetch meta for current week
  useEffect(() => {
    const init = async () => {
      try {
        const url = new URL(window.location.href);
        const stParam = Number(url.searchParams.get('seasontype')) as SeasonType;
        const wkParam = url.searchParams.get('week');

        const m = await LiveGamesService.fetchScoreboardMeta();
        setMeta(m);
        setSeasonYear(m.seasonYear);

        let st: SeasonType = (stParam === 3 ? 3 : 2);
        let wk: number | undefined = wkParam ? Number(wkParam) : undefined;

        if (!wk) {
          // default to ESPN's current week + season type
          st = (m.currentSeasonType === 3 ? 3 : 2);
          wk = m.currentWeek;
        }
        setSelectedSeasonType(st);
        setSelectedWeek(wk);

        // Write URL
        const u = new URL(window.location.href);
        u.searchParams.set('seasontype', String(st));
        if (wk) u.searchParams.set('week', String(wk)); else u.searchParams.delete('week');
        window.history.replaceState({}, '', u.toString());
      } catch (err) {
        // Best effort: still attempt to load default current scoreboard without params
        console.error('Failed to initialize scoreboard meta', err);
      }
    };
    init();
  }, []);

  // Fetch selected games on selection/meta change
  useEffect(() => {
    if (!seasonYear || !selectedSeasonType) return;
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonYear, selectedSeasonType, selectedWeek]);

  // Poll periodically, useful during live games; safe otherwise
  useEffect(() => {
    const id = setInterval(() => { refresh(); }, 30 * 1000);
    return () => clearInterval(id);
  }, [seasonYear, selectedSeasonType, selectedWeek]);

  // Prefetch adjacent weeks in same season type
  useEffect(() => {
    const doPrefetch = async () => {
      if (!meta) return;
      const st = selectedSeasonType;
      const wk = selectedWeek;
      if (!wk) return;
      const prefetchOne = async (w: number) => {
        const ckey = keyFor(st, w);
        if (cache.has(ckey)) return;
        try {
          const data = await LiveGamesService.fetchGamesFor({ seasonYear, seasonType: st, week: w });
          setCache(prev => {
            const next = new Map(prev);
            next.set(ckey, data);
            return next;
          });
        } catch {
          // ignore prefetch errors
        }
      };
      if (st === 2) {
        if (wk > 1) prefetchOne(wk - 1);
        if (wk < (meta.regularWeeks || 18)) prefetchOne(wk + 1);
      } else if (st === 3) {
        const idx = meta.postseasonRounds.findIndex(r => r.value === wk);
        if (idx >= 0) {
          if (idx - 1 >= 0) prefetchOne(meta.postseasonRounds[idx - 1].value);
          if (idx + 1 < meta.postseasonRounds.length) prefetchOne(meta.postseasonRounds[idx + 1].value);
        }
      }
    };
    doPrefetch();
  }, [meta, selectedSeasonType, selectedWeek, cache, seasonYear]);

  const items = useMemo(() => {
    // Map with owners and sort by status state: in -> pre -> post
    const statusRank: Record<string, number> = { in: 0, pre: 1, post: 2 };
    return games
      .map((g) => ({
        ...g,
        homeOwner: ownerByTeam[g.homeAbbr],
        awayOwner: ownerByTeam[g.awayAbbr],
      }))
      .sort((a, b) => (statusRank[a.statusState || ''] ?? 99) - (statusRank[b.statusState || ''] ?? 99));
  }, [games, ownerByTeam]);

  const hasGames = items.length > 0;
  const statusLabels: Record<string, string> = {
    in: 'In Progress',
    pre: 'Upcoming',
    post: 'Final',
  };

  // Compute field progress (0..1) toward opponent end zone using possessionText
  // Example: TEN has ball on DEN 38 -> label "DEN 38"; progress = 100 - 38 = 62%
  const computeFieldProgress = (g: LiveGame): { pct: number | null; label: string } => {
    if (g.statusState !== 'in' || g.neutralPhase) return { pct: null, label: '' };
    const txt = g.possessionText || '';
    const m = txt.match(/([A-Z]{2,3})\s*(\d{1,2})/);
    if (m) {
      const sideAbbr = m[1];
      const y = parseInt(m[2], 10);
      if (!Number.isNaN(y)) {
        const onOwnSide = g.possessionAbbr && sideAbbr === g.possessionAbbr;
        const progressed = onOwnSide ? y : 100 - y;
        const clamped = Math.max(0, Math.min(100, progressed));
        return { pct: clamped / 100, label: txt };
      }
    }
    // If we cannot parse possessionText reliably, don't render the bar
    return { pct: null, label: txt };
  };

  const onSelect = (st: SeasonType, wk?: number) => {
    setSelectedSeasonType(st);
    setSelectedWeek(wk);
    // Update URL
    const u = new URL(window.location.href);
    u.searchParams.set('seasontype', String(st));
    if (wk) u.searchParams.set('week', String(wk)); else u.searchParams.delete('week');
    window.history.replaceState({}, '', u.toString());
  };

  const currentHeader = useMemo(() => {
    if (!meta) return "This Week's Games";
    if (selectedSeasonType === 2) {
      return selectedWeek ? `Week ${selectedWeek}` : "This Week's Games";
    }
    if (!selectedWeek || meta.postseasonRounds.length === 0) return 'Playoffs';
    const r = meta.postseasonRounds.find(r => r.value === selectedWeek);
    return r ? r.label : 'Playoffs';
  }, [meta, selectedSeasonType, selectedWeek]);

  return (
    <div className="inprogress card">
      <div className="header">
        <h2>{currentHeader}</h2>
        {/* Mobile dropdown */}
        {meta && (
          <select
            className="week-select"
            value={`${selectedSeasonType}:${selectedWeek ?? ''}`}
            onChange={(e) => {
              const [stStr, wkStr] = e.target.value.split(':');
              const st = Number(stStr) as SeasonType;
              const wk = wkStr ? Number(wkStr) : undefined;
              onSelect(st, wk);
            }}
          >
            <optgroup label="Regular Season">
              {Array.from({ length: meta.regularWeeks || 18 }, (_, i) => i + 1).map((w) => (
                <option key={`reg-${w}`} value={`2:${w}`}>Week {w}</option>
              ))}
            </optgroup>
            {meta.postseasonRounds.length > 0 && (
              <optgroup label="Playoffs">
                {meta.postseasonRounds.map((r) => (
                  <option key={`post-${r.value}`} value={`3:${r.value}`}>{r.label}</option>
                ))}
              </optgroup>
            )}
          </select>
        )}
      </div>
      {meta && (
        <div className="tabs-container">
          <div className="tabs-group">
            <span className="group-label">Regular:</span>
            <div className="tabs">
              {Array.from({ length: meta.regularWeeks || 18 }, (_, i) => i + 1).map((w) => (
                <button
                  key={`reg-${w}`}
                  className={`tab ${selectedSeasonType === 2 && selectedWeek === w ? 'active' : ''}`}
                  onClick={() => onSelect(2, w)}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>
          {meta.postseasonRounds.length > 0 && (
            <div className="tabs-group">
              <span className="group-label">Playoffs:</span>
              <div className="tabs">
                {meta.postseasonRounds.map((r) => (
                  <button
                    key={`post-${r.value}`}
                    className={`tab ${selectedSeasonType === 3 && selectedWeek === r.value ? 'active' : ''}`}
                    onClick={() => onSelect(3, r.value)}
                    title={r.label}
                  >
                    {r.shortLabel}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && !hasGames && !error && (
        <div className="empty muted">No games scheduled this week.</div>
      )}

      <div className="games-grid">
        {items.map((g, index) => {
          const prev = index > 0 ? items[index - 1] : null;
          const showSectionBreak = prev && prev.statusState !== g.statusState;
          const fp = computeFieldProgress(g);
          const needsLabel = index === 0 || showSectionBreak;
          const label = statusLabels[g.statusState || ''] || 'Other';
          return (
            <React.Fragment key={g.id}>
              {needsLabel ? (
                <div
                  className={`section-divider${index === 0 ? ' first' : ''}`}
                  role="heading"
                  aria-level={3}
                >
                  {label}
                </div>
              ) : null}
              <div className={`game-card ${g.statusState === 'in' && g.isRedZone ? 'redzone' : ''} ${g.neutralPhase ? 'between' : ''}`}>
                {(() => {
                  const topText = g.statusState === 'in' ? `Q${g.period} ${g.clock || ''}` : (g.statusText || '');
                  return (
                    <>
                      {topText ? (
                        <div className="meta top">
                          <span className={g.statusState === 'in' ? 'clock' : 'detail'}>{topText}</span>
                          {g.statusState === 'in' && !g.neutralPhase && fp.label ? (
                            <span className="spot">• {fp.label}</span>
                          ) : null}
                          {g.statusState === 'in' && g.neutralPhase && (g.transitionNote || '').trim() ? (
                            <span className="spot note">{g.transitionNote}</span>
                          ) : null}
                        </div>
                      ) : null}
                      {g.statusState === 'in' && !g.neutralPhase && fp.pct != null ? (
                        <div className="field-progress" aria-label="Field position" title={`Progress to end zone ${(fp.pct * 100).toFixed(0)}%`}>
                          <div className="fill" style={{ width: `${(fp.pct * 100).toFixed(0)}%` }} />
                        </div>
                      ) : null}
                    </>
                  );
                })()}
                <div className="row">
                  <div className={`team ${getOwnerColorClass(g.awayOwner)}`}>
                    <div className="name">
                      {g.awayAbbr}
                      {g.statusState === 'in' && !g.neutralPhase && g.possessionAbbr === g.awayAbbr && (
                        <span className="ball" aria-label="possession" title="Possession">🏈</span>
                      )}
                    </div>
                  </div>
                  <div className={`score ${flashing.has(`${g.id}-away`) ? 'flash' : ''}`}>{g.awayScore}</div>
                </div>
                <div className="row">
                  <div className={`team ${getOwnerColorClass(g.homeOwner)}`}>
                    <div className="name">
                      {g.homeAbbr}
                      {g.statusState === 'in' && !g.neutralPhase && g.possessionAbbr === g.homeAbbr && (
                        <span className="ball" aria-label="possession" title="Possession">🏈</span>
                      )}
                    </div>
                  </div>
                  <div className={`score ${flashing.has(`${g.id}-home`) ? 'flash' : ''}`}>{g.homeScore}</div>
                </div>
                {/* Bottom status removed to avoid duplicates; all status shown up top */}
                {g.statusState === 'in' && !g.neutralPhase && (g.downDistanceText || g.shortDownDistanceText) && (
                  <div className="meta situation">
                    <span className="down-distance">
                      {g.downDistanceText || g.shortDownDistanceText}
                    </span>
                  </div>
                )}
                {g.statusState === 'in' && g.neutralPhase && (
                  <div className="meta situation transition">
                    {g.lastDriveSummary && (
                      <span className="down-distance">{g.scoringTeamAbbr ? `Last drive (${g.scoringTeamAbbr}): ` : 'Last drive: '}{g.lastDriveSummary}{g.driveResult ? ` — ${g.driveResult}` : ''}</span>
                    )}
                    {!g.lastDriveSummary && g.lastPlayText && (
                      <span className="down-distance">Last play: {g.lastPlayText}</span>
                    )}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default InProgressGames;
